/**
 * Per-environment settings, read at runtime so one build serves every
 * environment. Each deploy writes `/config.js` (loaded by a classic <script> in
 * index.html, before the app bundle) setting `window.__APP_CONFIG__`; keys are
 * the old `VITE_*` names without the prefix. A key the runtime config lacks
 * falls back to the build-time `import.meta.env.VITE_<key>`, which is how local
 * dev (.env) and tests keep working: the dev server serves an empty
 * `/config.js`.
 *
 * A subpath export (`@discovr/common/runtime-config`) rather than the package
 * root, so tests that mock `@discovr/common` wholesale don't drop it.
 */
export type RuntimeConfigKey =
  | 'API_URL'
  | 'APP_ENV'
  | 'SENTRY_DSN'
  | 'CLARITY_PROJECT_ID'
  | 'BRAND_APP_URL'
  | 'LANDING_URL'
  | 'OUTREACH_HELP_ME_WRITE'
  | 'OUTREACH_ENABLED'
  | 'DISCOVERY_ENABLED'
  | 'FIREBASE_API_KEY'
  | 'FIREBASE_AUTH_DOMAIN'
  | 'FIREBASE_PROJECT_ID'
  | 'FIREBASE_STORAGE_BUCKET'
  | 'FIREBASE_MESSAGING_SENDER_ID'
  | 'FIREBASE_APP_ID'
  | 'FIREBASE_MEASUREMENT_ID';

declare global {
  interface Window {
    __APP_CONFIG__?: Partial<Record<RuntimeConfigKey, string>>;
  }
}

// Getters, not values: Vite inlines each literal `import.meta.env.VITE_*`
// access, and reading at call time keeps vi.stubEnv working in tests.
const buildTime: Record<RuntimeConfigKey, () => unknown> = {
  API_URL: () => import.meta.env.VITE_API_URL,
  APP_ENV: () => import.meta.env.VITE_APP_ENV,
  SENTRY_DSN: () => import.meta.env.VITE_SENTRY_DSN,
  CLARITY_PROJECT_ID: () => import.meta.env.VITE_CLARITY_PROJECT_ID,
  BRAND_APP_URL: () => import.meta.env.VITE_BRAND_APP_URL,
  LANDING_URL: () => import.meta.env.VITE_LANDING_URL,
  OUTREACH_HELP_ME_WRITE: () => import.meta.env.VITE_OUTREACH_HELP_ME_WRITE,
  OUTREACH_ENABLED: () => import.meta.env.VITE_OUTREACH_ENABLED,
  DISCOVERY_ENABLED: () => import.meta.env.VITE_DISCOVERY_ENABLED,
  FIREBASE_API_KEY: () => import.meta.env.VITE_FIREBASE_API_KEY,
  FIREBASE_AUTH_DOMAIN: () => import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  FIREBASE_PROJECT_ID: () => import.meta.env.VITE_FIREBASE_PROJECT_ID,
  FIREBASE_STORAGE_BUCKET: () => import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  FIREBASE_MESSAGING_SENDER_ID: () => import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  FIREBASE_APP_ID: () => import.meta.env.VITE_FIREBASE_APP_ID,
  FIREBASE_MEASUREMENT_ID: () => import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

/**
 * The value for `key`, verbatim like import.meta.env: a key the deploy set to
 * '' stays '' (admin's share links rely on that to fail closed), and a key
 * neither source has is undefined.
 */
export function runtimeConfig(key: RuntimeConfigKey): string | undefined {
  const runtime = typeof window !== 'undefined' ? window.__APP_CONFIG__ : undefined;
  if (runtime && key in runtime) return runtime[key];
  const fallback = buildTime[key]();
  return typeof fallback === 'string' ? fallback : undefined;
}

/** Fail-closed surface flags: only the literal 'true' turns the surface on. */
export const isOutreachEnabled = (): boolean => runtimeConfig('OUTREACH_ENABLED') === 'true';
export const isDiscoveryEnabled = (): boolean => runtimeConfig('DISCOVERY_ENABLED') === 'true';
